import {
  Button,
  Card,
  Box,
  Stack,
  Chip,
  Typography,
  alpha,
  useTheme,
  Dialog,
  DialogContent,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Paper,
  Popover,
  Tooltip,
} from '@mui/material';
import ProjectPopup from './ProjectPopup';
import ProjectEditPopup from './ProjectEditPopup';
import {
  LangProvider,
  useLang,
  useL,
} from '../../components/Table/projectDashboard/LangContext';
import {
  FILTER_META,
  STATUS_MAP,
  ZERO_EXCLUDE_LABELS,
} from '../../components/Table/projectDashboard/constants';

import { useDispatch, useSelector } from 'react-redux';
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useForm } from 'react-hook-form';
import dayjs from 'dayjs';

// Icons
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AddIcon from '@mui/icons-material/Add';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ListIcon from '@mui/icons-material/List';
import InsightsIcon from '@mui/icons-material/Insights';
import SearchField from '../../components/SearchField';
import { ProjectTable, ProjectDashboardTable } from '../../components/Table';
import POOverviewTab from '../../components/Table/projectDashboard/POOverviewTab';
import ContactFormModal from '../../components/Forms/ContactFormModal';
import ContactFormFields from '../../components/Forms/ContactFormFields';
import * as actions from '../../redux/actions';
import { DevWinBadge, DevTabBadge } from '../../components/DevPageBadge';
import api from '../../utils/functions/api';
import { accessRightSubMenu } from '../../utils/functions/accessRight';
import { validateThaiAddress } from '../../utils/functions/thaiZipCascade';
import useZipCascade from '../../hooks/useZipCascade';
import { useSocket } from '../../contexts/SocketContext';
import PopupDragContext from './PopupDragContext';
import { emptyValues as emptyClientValues } from '../Customer/client.constants';

const _hasCommitment = (r) =>
  r.work_status !== 'done' ||
  r.finance_status !== 'completed_po' ||
  r.legal_warranty ||
  r.legal_debt ||
  r.legal_lawsuit ||
  r.report_status === 'pending'; // ต้องส่งรายงานแต่ยังไม่ได้ส่ง = ยังมีภาระผูกพัน

const CatBox = React.memo(({ title, color, children }) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: `${color}30`,
      borderRadius: 1.5,
      p: 1,
      minWidth: 130,
      boxShadow: `inset 3px 0 0 ${color}`,
    }}
  >
    <Typography
      variant="caption"
      sx={{
        fontWeight: 700,
        color,
        display: 'block',
        mb: 0.4,
        pb: 0.3,
        borderBottom: `1px solid ${color}20`,
        fontSize: '0.67rem',
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
));

const CRow = React.memo(
  ({ label, count, checked, excluded, color, onChange, onExclude }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        borderRadius: 0.5,
        '&:hover': { bgcolor: `${color}08` },
      }}
    >
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={checked}
            onChange={onChange}
            sx={{ p: 0.25, color, '&.Mui-checked': { color } }}
          />
        }
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="caption"
              sx={{ fontSize: '0.72rem', lineHeight: 1.3 }}
            >
              {label}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.68rem',
                fontWeight: 700,
                color,
                minWidth: 16,
                textAlign: 'right',
              }}
            >
              {count}
            </Typography>
          </Box>
        }
        sx={{ m: 0, flex: 1, px: 0.25 }}
      />
      {onExclude && (
        <Box
          onClick={onExclude}
          sx={{
            width: 18,
            height: 18,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 0.5,
            cursor: 'pointer',
            mr: 0.25,
            fontSize: '0.7rem',
            fontWeight: 700,
            lineHeight: 1,
            userSelect: 'none',
            color: excluded ? '#fff' : '#94a3b8',
            bgcolor: excluded ? '#b91c1c' : 'transparent',
            '&:hover': {
              bgcolor: excluded ? '#991b1b' : '#fee2e2',
              color: excluded ? '#fff' : '#b91c1c',
            },
          }}
        >
          ✕
        </Box>
      )}
    </Box>
  ),
);

// ฟ้องร้อง (legal_lawsuit) ถือว่ามีภาระผูกพันเสมอ ห้าม exclude ออกโดย default —
// ต่างจาก Warranty/เจ้าหนี้ ที่เป็นแค่บันทึกเตือนหลังงาน+เงินจบครบแล้ว
const LEGAL_EXCLUDE_KEYS = new Set(['legal_warranty', 'legal_debt', 'legal_lawsuit']);
const DEFAULT_EXCLUDE_FILTER = new Set(['legal_warranty', 'legal_debt']);

const PROJ_CACHE_KEY = 'proj_dash_cache';
const PROJ_CACHE_TTL = 4 * 60 * 60 * 1000; // 4 ชม.

const matchesProjectType = (projectNumber, type) => {
  const value = String(projectNumber || '');
  if (type === 'js') return /^JS_/i.test(value);
  if (type === 'jsid') return /^JSID/i.test(value);
  if (type === 'test') return /^TEST/i.test(value);
  return false;
};

const STATUS_SUMMARY = [
  {
    key: 'has_commitment',
    label: 'มีภาระผูกพัน',
    labelEn: 'Committed',
    color: '#d97706',
    bg: '#fef3c7',
    isMain: true,
    match: (r) => _hasCommitment(r),
  },
  {
    key: 'no_commitment',
    label: 'ไม่มีภาระผูกพัน',
    labelEn: 'No commitment',
    color: '#059669',
    bg: '#dcfce7',
    isMain: true,
    match: (r) => !_hasCommitment(r),
  },
  {
    key: 'work_waiting',
    label: 'รอเข้าดำเนินการ',
    labelEn: 'Waiting',
    color: '#64748b',
    bg: '#f1f5f9',
    match: (r) => !r.work_status || r.work_status === 'waiting',
  },
  {
    key: 'work_in_progress',
    label: 'ดำเนินการ',
    labelEn: 'Working',
    color: '#2563eb',
    bg: '#eff6ff',
    match: (r) => r.work_status === 'in_progress',
  },
  {
    key: 'work_done',
    label: 'เสร็จ 100%',
    labelEn: 'Done 100%',
    color: '#059669',
    bg: '#dcfce7',
    match: (r) => r.work_status === 'done',
  },
  {
    key: 'finance_none',
    label: 'ยังไม่เคยเบิก',
    labelEn: 'Not billed',
    color: '#94a3b8',
    bg: '#f8fafc',
    match: (r) => !r.finance_status || r.finance_status === 'none',
  },
  {
    key: 'finance_partial',
    label: 'เบิกบางส่วน',
    labelEn: 'Partial',
    color: '#f59e0b',
    bg: '#fffbeb',
    match: (r) => r.finance_status === 'partial',
  },
  {
    key: 'finance_billed_waiting',
    label: 'วางบิลรอรับ',
    labelEn: 'Billed',
    color: '#d97706',
    bg: '#fff7ed',
    match: (r) => r.finance_status === 'billed_waiting',
  },
  {
    key: 'finance_last_installment',
    label: 'รอรับงวดสุดท้าย',
    labelEn: 'Final due',
    color: '#c2410c',
    bg: '#fff1f2',
    match: (r) => r.finance_status === 'last_installment',
  },
  {
    key: 'finance_completed_po',
    label: 'เบิกครบ PO',
    labelEn: 'PO paid',
    color: '#059669',
    bg: '#dcfce7',
    match: (r) => r.finance_status === 'completed_po',
  },
  {
    key: 'bg_no_bg_usage',
    label: 'ไม่ใช้ BG แล้ว',
    labelEn: 'BG closed',
    color: '#374151',
    bg: '#f3f4f6',
    match: (r) => r.bg_usage_category === 'no_bg_usage',
  },
  {
    key: 'legal_warranty',
    label: 'Warranty',
    labelEn: 'Warranty',
    color: '#7c3aed',
    bg: '#f5f3ff',
    match: (r) => Boolean(r.legal_warranty),
  },
  {
    key: 'legal_debt',
    label: 'เจ้าหนี้',
    labelEn: 'Payable',
    color: '#dc2626',
    bg: '#fef2f2',
    match: (r) => Boolean(r.legal_debt),
  },
  {
    key: 'legal_lawsuit',
    label: 'ฟ้องร้อง',
    labelEn: 'Lawsuit',
    color: '#991b1b',
    bg: '#fef2f2',
    match: (r) => Boolean(r.legal_lawsuit),
  },
];

// Paper ของ popup P111 ที่ลากย้ายตำแหน่งได้ — จับลากจากแถบเทาบางๆ ด้านบนสุดเท่านั้น
// (ไม่ผูกกับทั้งกล่อง กันไปชนกับปุ่ม/ฟิลด์ที่คลิกได้เต็มพื้นที่ popup) ตำแหน่งรีเซ็ตเองทุกครั้ง
// ที่เปิด popup ใหม่ เพราะ Dialog unmount PaperComponent ทิ้งตอนปิด
function DraggablePaper(props) {
  const posRef = useRef({ x: 0, y: 0 });
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const recenter = useCallback(() => {
    posRef.current = { x: 0, y: 0 };
    setPos({ x: 0, y: 0 });
  }, []);

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const origin = posRef.current;
    const onMove = (ev) => {
      const next = {
        x: origin.x + (ev.clientX - startX),
        y: origin.y + (ev.clientY - startY),
      };
      posRef.current = next;
      setPos(next);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <Paper
      {...props}
      style={{ ...props.style, transform: `translate(${pos.x}px, ${pos.y}px)` }}
    >
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          height: 14,
          bgcolor: '#cbd5e1',
          cursor: 'move',
          borderRadius: '4px 4px 0 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          userSelect: 'none',
          '&:hover': { bgcolor: '#94a3b8' },
        }}
      >
        <DragIndicatorIcon sx={{ fontSize: 14, color: '#64748b' }} />
      </Box>
      <PopupDragContext.Provider value={recenter}>
        {props.children}
      </PopupDragContext.Provider>
    </Paper>
  );
}

const fmtMoney = (v) =>
  Number(v || 0).toLocaleString('th-TH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// นิยามระดับโมดูล (ไม่ใช่ในฟังก์ชัน render) — ถ้านิยามซ้ำใหม่ทุกครั้งที่ ProjectList render
// React จะมองว่าเป็นคนละ component type แล้ว unmount/remount ทุกใบทุกครั้ง ทำให้ DOM node ที่จับไว้
// ใน onClick (e.currentTarget) หลุดจาก document ก่อน Popover จะใช้งาน anchorEl ได้ทัน
const SCard = ({ label, value, color, col, onClick, onContextMenu, active }) => (
  <Box
    onClick={onClick}
    onContextMenu={onContextMenu}
    sx={{
      position: 'relative',
      bgcolor: color,
      color: '#fff',
      borderRadius: 2,
      px: 2,
      pt: 1.25,
      pb: col ? 2 : 1.25,
      minWidth: 170,
      textAlign: 'center',
      cursor: onClick || onContextMenu ? 'pointer' : 'default',
      outline: active ? '3px solid #fff' : 'none',
      outlineOffset: '-3px',
      boxShadow: active ? `0 0 0 3px ${color}` : 'none',
      transition: 'outline 0.1s, box-shadow 0.1s',
      '&:hover': onClick || onContextMenu ? { filter: 'brightness(1.1)' } : {},
    }}
  >
    {col && (
      <Typography
        sx={{
          position: 'absolute',
          bottom: 4,
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: '0.6rem',
          opacity: 0.55,
          lineHeight: 1,
          whiteSpace: 'nowrap',
        }}
      >
        {col}
      </Typography>
    )}
    <Typography
      variant="caption"
      sx={{ opacity: 0.85, display: 'block', lineHeight: 1.4 }}
    >
      {label}
    </Typography>
    <Typography
      variant="subtitle1"
      sx={{ fontWeight: 700, lineHeight: 1.3, whiteSpace: 'nowrap' }}
    >
      {fmtMoney(value)}
    </Typography>
  </Box>
);
const Op = ({ children }) => (
  <Typography
    variant="h5"
    sx={{ color: 'text.secondary', alignSelf: 'center', fontWeight: 300 }}
  >
    {children}
  </Typography>
);
// ป้าย "T-#" หน้าชื่อแท็บ — เลขเดียวกับ tabNum ที่ DevTabBadge ใช้ (T-12/T-13) เพื่อให้อ้างอิงแท็บ
// ตรงกันทั้งใน dev badge (floating) และในตัวแท็บเอง — dev-only เหมือน DevCardBadge/DevTabBadge
// ทุกตัว ไม่โชว์ใน production (พลาดมาก่อน ไม่มี guard นี้ เลยหลุดขึ้นจริงบนเว็บ)
const TabNumTag = ({ children }) => {
  if (process.env.NODE_ENV !== 'development') return null;
  return (
  <Box
    component="span"
    sx={{
      fontFamily: 'monospace',
      fontWeight: 800,
      fontSize: '0.6rem',
      color: '#fff',
      backgroundColor: 'rgba(123, 31, 162, 0.85)',
      borderRadius: '4px',
      px: 0.5,
      py: '1px',
      letterSpacing: '0.02em',
    }}
  >
    {children}
  </Box>
  );
};

export default function ProjectList(props) {
  return (
    <LangProvider>
      <ProjectListInner {...props} />
    </LangProvider>
  );
}

function ProjectListInner({ title, subtitle }) {
  const L = useL();
  const { lang, toggleLang } = useLang();
  const [filterLang, setFilterLang] = useState(lang);
  const FL = useCallback(
    (th, en) => (filterLang === 'en' ? en : th),
    [filterLang],
  );
  const toggleFilterLang = useCallback(() => {
    setFilterLang((prev) => (prev === 'en' ? 'th' : 'en'));
  }, []);

  // ภาษาหลักเป็นผู้บังคับ: ทุกครั้งที่ปุ่มภาษาหลักเปลี่ยน ให้แผง Filter เปลี่ยนตามด้วย
  // หลังจากนั้นปุ่มภาษาในแผงยัง override เฉพาะแผงได้ โดยไม่กระทบส่วนอื่นของโมดูล
  useEffect(() => {
    setFilterLang(lang);
  }, [lang]);
  const history = useHistory();
  const dispatch = useDispatch();
  const projects = useSelector((state) => state.project);
  const me = useSelector((state) => state.me);
  const theme = useTheme();
  const socket = useSocket();

  const [activeTab, setActiveTab] = useState(0); // 0=ตารางโครงการ, 1=ภาพรวม PO
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const dbSearchedRef = useRef(''); // เก็บ term ที่ query DB ล่าสุด (สำหรับ scopeLabel)
  const [selectedId, setSelectedId] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  // อยู่นอก ProjectPopup ตั้งใจ — popup ใช้ key={selectedId} ทำให้ remount ใหม่ทั้งก้อนทุกครั้งที่
  // เลื่อนก่อนหน้า/ถัดไป ถ้าเก็บ sheet (แดชบอร์ด/รายละเอียด) ไว้ข้างในจะรีเซ็ตกลับแท็บแรกทุกครั้ง
  const [popupSheet, setPopupSheet] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  // ลิสต์ id ตามลำดับที่กรอง/เรียงอยู่ในตาราง P5 ตอนนี้ — ใช้ทำปุ่มเลื่อนถัดไป/ก่อนหน้า
  // ใน popup P111 (รับมาจาก ProjectDashboardTable ผ่าน onVisibleIdsChange)
  const [visibleIds, setVisibleIds] = useState([]);

  const [addClientOpen, setAddClientOpen] = useState(false);
  const [addClientSubmitError, setAddClientSubmitError] = useState('');
  const {
    handleSubmit: handleAddClientSubmit,
    control: addClientControl,
    reset: addClientReset,
    setValue: addClientSetValue,
    watch: addClientWatch,
  } = useForm({ defaultValues: emptyClientValues });
  const {
    cascade: addClientCascade,
    setZipSelections: setAddClientZipSelections,
  } = useZipCascade(addClientWatch, addClientSetValue);

  const [statusFilter, setStatusFilter] = useState('has_commitment');
  const [excludeFilter, setExcludeFilter] = useState(
    new Set(DEFAULT_EXCLUDE_FILTER),
  );
  const [jsFilter, setJsFilter] = useState(true);
  const [jsidFilter, setJsidFilter] = useState(false);
  const [testFilter, setTestFilter] = useState(false);
  const [customerFilter, setCustomerFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [loadedScope, setLoadedScope] = useState('default'); // 'default'=JS_+has_commitment | 'all' | 'no_commitment'

  // ตั้งค่าการแสดงผล "ฟ้องร้อง" ในป้ายสรุปบนสุด — กดที่ป้าย "ยอดรวม" เพื่อเปิดเมนูนี้
  const [lawsuitMenuAnchor, setLawsuitMenuAnchor] = useState(null);
  const [showLawsuitCard, setShowLawsuitCard] = useState(false);
  const [includeLawsuitInCard2, setIncludeLawsuitInCard2] = useState(true);

  // ป้ายตัวกรองที่ active อยู่ในตาราง P5 ตอนนี้ — โชว์แบบ read-only ใน popup P111 ด้วย
  // (เพราะลิสต์เลื่อนก่อนหน้า/ถัดไปในนั้นอิงจากตัวกรองชุดนี้ ผู้ใช้ควรเห็นบริบทว่ากรองอะไรอยู่)
  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (jsFilter) chips.push({ key: 'js', label: `${FL('ประเภท', 'Type')}: JS_` });
    if (jsidFilter) chips.push({ key: 'jsid', label: `${FL('ประเภท', 'Type')}: JSID` });
    if (testFilter) chips.push({ key: 'test', label: `${FL('ประเภท', 'Type')}: Test` });
    if (statusFilter) {
      const meta = STATUS_SUMMARY.find((s) => s.key === statusFilter);
      chips.push({ key: 'status', label: FL(meta?.label, meta?.labelEn) || statusFilter });
    }
    if (customerFilter) chips.push({ key: 'customer', label: `${FL('ผู้ว่าจ้าง', 'Customer')}: ${customerFilter}` });
    if (projectFilter) chips.push({ key: 'project', label: `${FL('โครงการ', 'Project')}: ${projectFilter}` });
    if (searchTerm) chips.push({ key: 'search', label: `${FL('ค้นหา', 'Search')}: "${searchTerm}"` });
    for (const key of excludeFilter) {
      const meta = STATUS_SUMMARY.find((s) => s.key === key);
      chips.push({
        key: `ex-${key}`,
        label: `${FL('ไม่รวม', 'Exclude')} ${FL(meta?.label, meta?.labelEn) || key}`,
      });
    }
    return chips;
  }, [FL, jsFilter, jsidFilter, testFilter, statusFilter, customerFilter, projectFilter, searchTerm, excludeFilter]);
  const [showGroup1, setShowGroup1] = useState(false);
  // ตัวเลขอ้างอิงคอลัมน์ (col 4/col 14/col 2 ใต้การ์ดสรุป + เลข 1-22 หัวตาราง) ซ่อนไว้เป็นค่าเริ่มต้น
  // โชว์ต่อเมื่อกดปุ่ม "โชว์เลขคอลัมน์" เท่านั้น — ไม่เกี่ยวกับ caption ที่มีความหมาย (เช่น "If
  // Complete & Paid"/"ไม่รวมยอดรวม") ซึ่งยังโชว์เสมอเพราะไม่ใช่เลขอ้างอิงคอลัมน์
  const [showColNums, setShowColNums] = useState(false);
  // จำค่า showGroup1 ก่อนกด badge ที่บังคับเปิดกลุ่ม 1 (Invoiced=col7_positive, Total Cash In=
  // col9_or_col7_nonzero — ทั้งคู่อ้างอิง col7 เลยต้องเปิดกลุ่ม 1 ให้เห็นคอลัมน์ที่ใช้กรองเหมือนกัน)
  // ใช้ ref เดียวร่วมกัน + ฟังก์ชันช่วยด้านล่าง กัน bug ที่เคยเจอ: ถ้าใช้ ref แยกกันคนละตัว แล้วสลับกด
  // ปุ่มหนึ่งไปอีกปุ่มโดยไม่กลับ default ก่อน ref ตัวที่สองจะจำค่า "true" (ที่ปุ่มแรกบังคับไว้) แทนค่า
  // default จริงๆ พอกดปิดตัวที่สอง เลยคืนกลับเป็น true ผิดๆ แทนที่จะเป็น default (false) เดิม
  const showGroup1BeforeForceRef = useRef(false);
  const FORCE_GROUP1_FILTERS = ['col7_positive', 'col9_or_col7_nonzero'];
  const activateForceGroup1Filter = (key) => {
    // จำค่าก่อนหน้าเฉพาะตอนยังไม่ได้ถูกบังคับเปิดจากปุ่มไหนอยู่ก่อน (สลับไปมาระหว่าง 2 ปุ่มนี้
    // จะไม่เขียนทับค่า default เดิมซ้ำ)
    if (!FORCE_GROUP1_FILTERS.includes(statusFilter)) {
      showGroup1BeforeForceRef.current = showGroup1;
    }
    setStatusFilter(key);
    setExcludeFilter((prev) => {
      const s = new Set(prev);
      s.delete(key);
      return s;
    });
    setShowGroup1(true);
  };
  const deactivateForceGroup1Filter = () => {
    setStatusFilter('has_commitment');
    setJsFilter(true);
    setJsidFilter(false);
    setTestFilter(false);
    setShowGroup1(showGroup1BeforeForceRef.current);
  };
  // true = sort ตรงกับ default อยู่ (progress asc + customer asc) — อัปเดตจาก callback ของ
  // ProjectDashboardTable.js (onSortDefaultChange) เพราะ sortCriteria จริงเป็น state ภายในตารางเอง
  const [isDefaultSort, setIsDefaultSort] = useState(true);
  const [dashboardSortCriteria, setDashboardSortCriteria] = useState(null);
  // ป้าย "ไม่รวม 0/ว่าง: X" — state จริงยังอยู่ใน ProjectDashboardTable.js (ใช้ลึกในตรรกะกรอง/header)
  // ตัวนี้แค่ mirror ไว้ render chip ที่แถวบน กดลบเรียกผ่าน dashboardTableRef.removeZeroExclude(key)
  const [excludedZeroFields, setExcludedZeroFields] = useState(new Set());
  const [excludedProjects, setExcludedProjects] = useState(new Set());
  const [excludedCustomers, setExcludedCustomers] = useState(new Set());
  const [normalView, setNormalView] = useState(false);
  const [clearSnapshot, setClearSnapshot] = useState(null); // null = ปกติ, object = กำลัง cleared

  const [cacheInfo, setCacheInfo] = useState(null); // { timestamp } เมื่อโหลดจาก cache
  const unblockRef = useRef(null);

  const pageRef = useRef();
  const dashboardTableRef = useRef(null);
  const sidebarWRef = useRef(null);
  const defaultDashRowsRef = useRef([]); // cache ของ default scope — ใช้ restore ตอนกด Esc
  const [pageScale, setPageScale] = useState(1);

  // reset zoom on unmount
  useEffect(() => {
    return () => {
      const root = document.getElementById('root');
      if (root) {
        root.style.zoom = '';
        root.style.width = '';
      }
      if (pageRef.current) {
        pageRef.current.style.zoom = '';
        pageRef.current.style.width = '';
      }
    };
  }, []);

  useEffect(() => {
    // capture sidebar width once (before any zoom is applied)
    if (sidebarWRef.current === null && pageRef.current) {
      sidebarWRef.current = Math.round(
        pageRef.current.getBoundingClientRect().left,
      );
    }
    const COL_W = {
      1: 68,
      2: 76,
      3: 44,
      4: 150,
      5: 130,
      6: 78,
      7: 80,
      8: 78,
      9: 68,
      10: 72,
      11: 70,
      12: 72,
      13: 60,
      14: 80,
      15: 70,
      16: 66,
      17: 74,
      19: 88,
      20: 60,
      26: 110,
    };
    const tableW = showGroup1
      ? 38 +
        COL_W[1] +
        COL_W[2] +
        COL_W[3] +
        COL_W[4] +
        COL_W[5] +
        COL_W[6] +
        COL_W[7] +
        COL_W[8] +
        COL_W[9] +
        COL_W[10] +
        COL_W[11] +
        COL_W[12] +
        COL_W[13] +
        COL_W[14] +
        COL_W[15] +
        COL_W[16] +
        COL_W[17] +
        COL_W[19] +
        COL_W[20] +
        COL_W[26]
      : Math.max(
          1500,
          38 +
            COL_W[1] +
            COL_W[2] +
            COL_W[3] +
            COL_W[4] +
            COL_W[5] +
            COL_W[6] +
            COL_W[10] +
            COL_W[11] +
            COL_W[12] +
            COL_W[13] +
            COL_W[14] +
            COL_W[15] +
            COL_W[16] +
            COL_W[17] +
            COL_W[19] +
            COL_W[20] +
            COL_W[26],
        );
    const naturalW = tableW + 20; // +20: reserve for TableContainer vertical scrollbar
    const calc = () => {
      const el = pageRef.current;
      if (!el) return;
      if (normalView) {
        setPageScale(1);
        el.style.zoom = '';
        el.style.width = '';
        return;
      }
      const sidebarW = sidebarWRef.current ?? 0;
      const vw = document.documentElement.clientWidth;
      const availW = vw - sidebarW;
      const scale = Math.min(1, availW / naturalW);
      setPageScale(scale);
      // zoom เฉพาะ content area — sidebar ไม่ถูกย่อ
      el.style.zoom = String(scale);
      el.style.width = scale < 1 ? `${naturalW}px` : '';
    };
    const raf = requestAnimationFrame(calc);
    window.addEventListener('resize', calc);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', calc);
      if (pageRef.current) {
        pageRef.current.style.zoom = '';
        pageRef.current.style.width = '';
      }
    };
  }, [showGroup1, normalView]);

  const [dashRows, setDashRows] = useState([]);
  const [dashLoading, setDashLoading] = useState(false);
  const [dashTotals, setDashTotals] = useState(null); // รับจาก ProjectDashboardTable

  const statusCountMap = useMemo(() => {
    const counts = { _js: 0, _jsid: 0, _test: 0 };
    for (const r of dashRows) {
      if (matchesProjectType(r.project_number, 'js')) counts._js++;
      if (matchesProjectType(r.project_number, 'jsid')) counts._jsid++;
      if (matchesProjectType(r.project_number, 'test')) counts._test++;
      for (const { key, match } of STATUS_SUMMARY) {
        if (match(r)) counts[key] = (counts[key] || 0) + 1;
      }
    }
    return counts;
  }, [dashRows]);

  const loadAllDash = useCallback(
    async ({ jsOnly = false, sf = '', name = '', scope = null } = {}) => {
      setDashLoading(true);
      try {
        const params = new URLSearchParams({
          size: 300,
          page: 1,
          skip_ts: '1',
        });
        if (jsOnly) params.set('js_only', '1');
        // col9/col7 computed from aggregation — use has_commitment as server base, filter client-side
        const serverSf = [
          'col9_positive',
          'col7_positive',
          'col9_or_col7_nonzero',
        ].includes(sf)
          ? 'has_commitment'
          : sf;
        if (serverSf) params.set('status_filter', serverSf);
        if (name) params.set('name', name);
        const res = await api.get(
          `${process.env.REACT_APP_API_URL}/project?${params}`,
        );
        const rows = res.data?.rows || [];
        if (scope === 'all') defaultDashRowsRef.current = rows;
        setDashRows(rows);
        if (scope) setLoadedScope(scope);
      } catch (e) {
        console.error('loadAllDash error', e);
      } finally {
        setDashLoading(false);
      }
    },
    [],
  );

  // แก้โปรเจกต์เดียวแล้วรีเฟรชแค่แถวนั้นในตาราง P5 — ไม่ต้องโหลดทั้ง 300 แถวใหม่
  // (ยิง GET /project/:id ตัวเดียว ได้ field ที่ aggregation คำนวณครบเหมือน list เพราะ
  // ใช้ createPipeline/heavyStages ชุดเดียวกัน) แล้วแทนที่ทั้งใน dashRows และ localStorage cache
  const refreshSingleDashRow = useCallback(async (id) => {
    if (!id) return;
    try {
      const res = await api.get(
        `${process.env.REACT_APP_API_URL}/project/${id}?skip_ts=1`,
      );
      const updated = res.data;
      if (!updated) return;
      setDashRows((prev) => {
        const next = prev.map((r) => (r._id === id ? updated : r));
        try {
          const raw = localStorage.getItem(PROJ_CACHE_KEY);
          if (raw) {
            const cached = JSON.parse(raw);
            cached.rows = next;
            localStorage.setItem(PROJ_CACHE_KEY, JSON.stringify(cached));
          }
        } catch {}
        return next;
      });
      if (defaultDashRowsRef.current?.length) {
        defaultDashRowsRef.current = defaultDashRowsRef.current.map((r) =>
          r._id === id ? updated : r,
        );
      }
    } catch (e) {
      console.error('refreshSingleDashRow error', e);
    }
  }, []);

  const handleActionRequiredChange = useCallback((id, actionRequired) => {
    setDashRows((prev) => prev.map((row) => (
      row._id === id ? { ...row, action_required: actionRequired } : row
    )));
    if (defaultDashRowsRef.current?.length) {
      defaultDashRowsRef.current = defaultDashRowsRef.current.map((row) => (
        row._id === id ? { ...row, action_required: actionRequired } : row
      ));
    }
  }, []);

  // แคช proj_dash_cache รู้ตัวว่าโปรเจกต์อัพเดทจากที่อื่น (แท็บอื่น/user อื่น/สคริปต์ backend)
  // ไม่ใช่แค่จากการแก้ในแท็บนี้เอง — ดึงเฉพาะแถวนั้นมาใหม่ทุกครั้งที่ backend ยิง project:updated
  useEffect(() => {
    if (!socket) return;
    const refresh = ({ id } = {}) => {
      if (id) refreshSingleDashRow(id);
    };
    socket.on('project:updated', refresh);
    return () => socket.off('project:updated', refresh);
  }, [socket, refreshSingleDashRow]);

  useEffect(() => {
    let fromCache = false;
    try {
      const raw = localStorage.getItem(PROJ_CACHE_KEY);
      if (raw) {
        const cached = JSON.parse(raw);
        if (
          Date.now() - cached.timestamp < PROJ_CACHE_TTL &&
          cached.rows?.length > 0
        ) {
          if (cached.scope === 'all') defaultDashRowsRef.current = cached.rows;
          dbSearchedRef.current = cached.dbSearchedTerm || '';
          setDashRows(cached.rows);
          setLoadedScope(cached.scope || 'all');
          setCacheInfo({ timestamp: cached.timestamp });
          fromCache = true;
        }
      }
    } catch {}
    if (!fromCache) loadAllDash({ jsOnly: false, sf: '', scope: 'all' });
  }, [loadAllDash]);

  const handleSearchEsc = useCallback(() => {
    setSearchTerm('');
    setJsFilter(true);
    setJsidFilter(false);
    setTestFilter(false);
    setStatusFilter('has_commitment');
    setExcludeFilter(new Set(DEFAULT_EXCLUDE_FILTER));
    setCustomerFilter('');
    setProjectFilter('');
    setClearSnapshot(null);
    if (loadedScope !== 'all') {
      setDashRows(defaultDashRowsRef.current);
      setLoadedScope('all');
    }
  }, [loadedScope]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (selectedId || createOpen) return; // ให้ dialog จัดการ Esc เอง
      handleSearchEsc();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [handleSearchEsc, selectedId, createOpen]);

  const handleDbSearch = useCallback(() => {
    const q = searchTerm.trim();
    if (!q) return;
    dbSearchedRef.current = q;
    setJsFilter(false);
    setJsidFilter(false);
    setTestFilter(false);
    setStatusFilter('');
    setExcludeFilter(new Set()); // ล้าง exclude ทั้งหมดก่อนค้นหา
    setCustomerFilter('');
    setProjectFilter('');
    setClearSnapshot(null);
    loadAllDash({ name: q, jsOnly: false, sf: '', scope: 'search' });
  }, [searchTerm, loadAllDash]);

  // ── Auto-save cache on navigate away ──────────────────────────────────────────
  useEffect(() => {
    unblockRef.current = history.block((location) => {
      // save แล้ว navigate ต่อทันที ไม่ถาม
      try {
        localStorage.setItem(
          PROJ_CACHE_KEY,
          JSON.stringify({
            rows: dashRows,
            scope: loadedScope,
            dbSearchedTerm: dbSearchedRef.current,
            timestamp: Date.now(),
          }),
        );
      } catch {}
      unblockRef.current?.();
      unblockRef.current = null;
      history.push(
        typeof location === 'string'
          ? location
          : location.pathname + (location.search || ''),
      );
      return false;
    });
    return () => {
      unblockRef.current?.();
      unblockRef.current = null;
    };
  }, [history, dashRows, loadedScope]);

  const isAdmin = accessRightSubMenu(me, 'PROJECT', '/project/project');

  useEffect(() => {
    dispatch(actions.projectAll({ page, size }));
    return () => {};
  }, [page, size]);

  const isFirstSearch = useRef(true);
  useEffect(() => {
    if (isFirstSearch.current) {
      isFirstSearch.current = false;
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      dispatch(
        actions.projectAll({ page: 1, size, name: searchTerm || undefined }),
      );
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const handleOnclickDetail = (id) => {
    history.push(`/project/project/detail/${id}`);
  };

  const handleOpenProject = (id, { preserveSheet = false } = {}) => {
    const row =
      dashRows.find((r) => r._id === id || r.id === id) ||
      projects?.rows?.find((r) => r._id === id || r.id === id) ||
      null;
    setSelectedRow(row);
    setSelectedId(id);
    if (!preserveSheet) setPopupSheet(0);
  };
  const handleCloseProject = () => {
    setSelectedId(null);
    setSelectedRow(null);
  };

  // ปุ่มเลื่อนถัดไป/ก่อนหน้าใน modal — อิงลำดับจาก visibleIds (ลิสต์ที่กรอง/เรียงอยู่ใน P5 ตอนนี้)
  // preserveSheet: true — อยู่แท็บ "รายละเอียด" แล้วกดเลื่อน ต้องไม่กระโดดกลับแท็บ "แดชบอร์ด"
  const visibleIndex = visibleIds.indexOf(selectedId);
  const handlePrevProject = () => {
    if (visibleIndex > 0) {
      handleOpenProject(visibleIds[visibleIndex - 1], { preserveSheet: true });
    }
  };
  const handleNextProject = () => {
    if (visibleIndex >= 0 && visibleIndex < visibleIds.length - 1) {
      handleOpenProject(visibleIds[visibleIndex + 1], { preserveSheet: true });
    }
  };

  // ปุ่ม "แยกไปหน้าต่างใหม่" มุมขวาบนของ popup — เปิด P111 เป็นหน้าต่างเบราว์เซอร์แยกจริง
  // แล้วปิด modal เดิม ชื่อหน้าต่าง (arg ที่ 2) ผูกกับ project id กันเปิดซ้ำหลายบานถ้ากดซ้ำ
  const handlePopOutProject = (id) => {
    const w = 1500;
    const h = Math.min(window.screen.height - 100, 950);
    const left = Math.max(0, (window.screen.width - w) / 2);
    window.open(
      `/project-popup/${id}`,
      `project-popup-${id}`,
      `width=${w},height=${h},left=${left},top=40`,
    );
    handleCloseProject();
  };

  // ฟังข้อความจากหน้าต่าง P111 ที่แยกออกไป:
  // - project-saved: save สำเร็จในนั้น ให้รีเฟรชแค่แถวเดียวกันใน P5
  // - request-nav-list: หน้าต่างแยกขอลิสต์ id ปัจจุบันไปทำปุ่มเลื่อนถัดไป/ก่อนหน้าของตัวเอง
  // - pop-in: กด "กลับมาเป็น popup" ในหน้าต่างแยก ให้เปิด modal คืนในแท็บนี้
  useEffect(() => {
    const handler = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type === 'project-saved' && e.data?.id) {
        refreshSingleDashRow(e.data.id);
      } else if (e.data?.type === 'request-nav-list') {
        e.source?.postMessage(
          { type: 'nav-list', ids: visibleIds, filterChips: activeFilterChips },
          window.location.origin,
        );
      } else if (e.data?.type === 'pop-in' && e.data?.id) {
        handleOpenProject(e.data.id);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [refreshSingleDashRow, visibleIds, activeFilterChips]);

  const handleSavedProject = (isEdit = false) => {
    if (selectedId) {
      refreshSingleDashRow(selectedId);
    } else {
      loadAllDash({ jsOnly: false, sf: '', scope: 'all' });
    }
    if (!isEdit) {
      dispatch(
        actions.projectAll({ page, size, name: searchTerm || undefined }),
      );
    }
  };

  const handleAddClientOpen = () => {
    setAddClientZipSelections({ province: null, district: null });
    setAddClientSubmitError('');
    setAddClientOpen(true);
  };
  const handleAddClientClose = () => {
    setAddClientOpen(false);
    addClientReset(emptyClientValues);
  };

  const onAddClientSubmit = async (data) => {
    const zipError = validateThaiAddress(data);
    if (zipError) {
      setAddClientSubmitError(zipError);
      return;
    }
    try {
      await dispatch(actions.contactCreate({ ...data, role: 'client' }));
      handleAddClientClose();
    } catch (error) {
      console.error('Failed to create client:', error);
    }
  };

  const renderActions = () => (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="center" gap={0.75}>
        <SearchField
          name="project-search-primary"
          type="search"
          autoComplete="off"
          label={L('ค้นหาโครงการ', 'Search Project')}
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={L('ค้นหาตามชื่อหรือรหัสโครงการ...', 'Search by name or project code...')}
          sx={{
            '& .MuiOutlinedInput-root': { borderRadius: 2 },
            width: 260,
            flexShrink: 0,
          }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{
            borderRadius: 1.5,
            height: 36,
            whiteSpace: 'nowrap',
            ml: 'auto',
            flexShrink: 0,
          }}
        >
          เพิ่มโครงการใหม่
        </Button>
      </Stack>
    </Box>
  );

  const buildStatusChips = () => {
    const toggleStatus = (key) => {
      setStatusFilter((prev) => (prev === key ? '' : key));
      setJsFilter(false);
      setJsidFilter(false);
      setTestFilter(false);
      setExcludeFilter((prev) => {
        const s = new Set(prev);
        s.delete(key);
        return s;
      });
    };

    const toggleExclude = (key) => {
      setExcludeFilter((prev) => {
        const s = new Set(prev);
        if (s.has(key)) s.delete(key);
        else s.add(key);
        return s;
      });
      setStatusFilter((prev) => (prev === key ? '' : prev));
    };

    const handleClearToggle = () => {
      if (clearSnapshot) {
        setStatusFilter(clearSnapshot.statusFilter);
        setJsFilter(clearSnapshot.jsFilter);
        setJsidFilter(clearSnapshot.jsidFilter);
        setTestFilter(clearSnapshot.testFilter);
        setExcludeFilter(clearSnapshot.excludeFilter);
        setClearSnapshot(null);
      } else {
        setClearSnapshot({ statusFilter, jsFilter, jsidFilter, testFilter, excludeFilter });
        setStatusFilter('');
        setJsFilter(false);
        setJsidFilter(false);
        setTestFilter(false);
        setExcludeFilter(new Set());
      }
    };

    const scopeLabel =
      loadedScope === 'search'
        ? `${FL('ผลค้นหา DB', 'DB results')}: "${dbSearchedRef.current}" (${dashRows.length})`
        : loadedScope === 'default'
        ? `${FL('โหลดอยู่', 'Loaded')}: JS_+${FL('มีผูกพัน', 'Committed')} (${dashRows.length})`
        : loadedScope === 'no_commitment'
        ? `${FL('โหลดอยู่', 'Loaded')}: ${FL('ไม่มีผูกพัน', 'No commitment')} (${dashRows.length})`
        : `${FL('โหลดอยู่: ทั้งหมด', 'Loaded: All')} (${dashRows.length})`;

    const LOAD_BTN = (color) => ({
      bgcolor: 'transparent',
      color,
      border: `1.5px dashed ${color}`,
      fontWeight: 500,
      cursor: 'pointer',
      fontSize: '0.72rem',
      '&:hover': { bgcolor: `${color}15` },
    });

    const CATS = [
      {
        title: FL('ภาระผูกพัน', 'Commitment'),
        color: '#d97706',
        items: STATUS_SUMMARY.filter((s) => s.isMain),
      },
      {
        title: FL('สถานะงาน', 'Work status'),
        color: '#2563eb',
        items: STATUS_SUMMARY.filter((s) =>
          ['work_waiting', 'work_in_progress', 'work_done'].includes(s.key),
        ),
      },
      {
        title: FL('การเบิกจ่าย', 'Finance'),
        color: '#f59e0b',
        items: STATUS_SUMMARY.filter(
          (s) => s.key.startsWith('finance_') || s.key === 'bg_no_bg_usage',
        ),
      },
      {
        title: FL('กฎหมาย/ความเสี่ยง', 'Legal/Risk'),
        color: '#7c3aed',
        items: STATUS_SUMMARY.filter((s) => s.key.startsWith('legal_')),
      },
    ];

    return (
      <Box>
        {/* ── Scope row ── */}
        <Stack
          direction="row"
          alignItems="center"
          gap={0.75}
          sx={{ mb: 1.25, flexWrap: 'wrap' }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.7rem',
              color: 'text.secondary',
              fontWeight: 600,
            }}
          >
            {scopeLabel}
          </Typography>
          {loadedScope === 'all' && (
            <Tooltip
              title={FL(
                'ดึงข้อมูลโครงการล่าสุดจากระบบมาแสดงใหม่',
                'Reload latest project data',
              )}
              arrow
              placement="top"
            >
              <Chip
                size="small"
                label={`🔄 ${FL('รีเฟรช', 'Refresh')}`}
                onClick={() =>
                  loadAllDash({ jsOnly: false, sf: '', scope: 'all' })
                }
                sx={LOAD_BTN('#6366f1')}
              />
            </Tooltip>
          )}
          {loadedScope !== 'all' && (
            <Chip
              size="small"
              label={`↺ ${FL('กลับทั้งหมด', 'Back to all')}`}
              onClick={() => {
                setStatusFilter('has_commitment');
                setJsFilter(true);
                setJsidFilter(false);
                setTestFilter(false);
                setDashRows(defaultDashRowsRef.current);
                setLoadedScope('all');
              }}
              sx={LOAD_BTN('#1e3a8a')}
            />
          )}
          {cacheInfo && (
            <Chip
              size="small"
              label={`💾 ${FL('แคช', 'Cache')} ${dayjs(cacheInfo.timestamp).format(
                'DD/MM HH:mm',
              )}`}
              onDelete={() => {
                localStorage.removeItem(PROJ_CACHE_KEY);
                setCacheInfo(null);
                loadAllDash({ jsOnly: false, sf: '', scope: 'all' });
              }}
              sx={{
                fontSize: '0.67rem',
                height: 20,
                bgcolor: '#f0fdf4',
                color: '#15803d',
                border: '1px solid #86efac',
                '& .MuiChip-deleteIcon': { fontSize: 14, color: '#15803d' },
              }}
            />
          )}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Tooltip
              title={
                filterLang === 'en'
                  ? 'เปลี่ยนแผง Filter เป็นภาษาไทย'
                  : 'Switch the Filter panel to English'
              }
              arrow
              placement="top"
            >
              <Chip
                size="small"
                label={filterLang === 'en' ? '🇹🇭 ไทย' : '🇬🇧 EN'}
                onClick={toggleFilterLang}
                sx={LOAD_BTN('#7c3aed')}
              />
            </Tooltip>
            <Chip
              size="small"
              label={
                clearSnapshot
                  ? `↩ ${FL('คืนค่า', 'Restore')}`
                  : FL('ล้างทั้งหมด', 'Clear all')
              }
              onClick={handleClearToggle}
              sx={
                clearSnapshot
                  ? {
                      ...LOAD_BTN('#d97706'),
                      borderStyle: 'solid',
                      fontWeight: 700,
                    }
                  : LOAD_BTN('#94a3b8')
              }
            />
          </Box>
        </Stack>

        {/* ── Checkbox categories ── */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          flexWrap="wrap"
          useFlexGap
        >
          {/* JS_ */}
          <CatBox title={FL('ประเภท', 'Type')} color="#b45309">
            <CRow
              label="JS_"
              count={statusCountMap._js || 0}
              checked={jsFilter}
              color="#f59e0b"
              onChange={() => setJsFilter((v) => !v)}
            />
            <CRow
              label="JSID"
              count={statusCountMap._jsid || 0}
              checked={jsidFilter}
              color="#f59e0b"
              onChange={() => setJsidFilter((v) => !v)}
            />
            <CRow
              label="Test"
              count={statusCountMap._test || 0}
              checked={testFilter}
              color="#f59e0b"
              onChange={() => setTestFilter((v) => !v)}
            />
          </CatBox>

          {CATS.map(({ title, color, items }) => (
            <CatBox key={title} title={title} color={color}>
              {items.map(({ key, label, labelEn }) => (
                <CRow
                  key={key}
                  label={FL(label, labelEn)}
                  count={statusCountMap[key] || 0}
                  checked={statusFilter === key}
                  excluded={excludeFilter.has(key)}
                  color={color}
                  onChange={() => toggleStatus(key)}
                  onExclude={() => toggleExclude(key)}
                />
              ))}
            </CatBox>
          ))}
        </Stack>
      </Box>
    );
  };

  const excludeCounts = useMemo(() => {
    if (excludeFilter.size === 0) return {};
    const selectedTypes = [
      jsFilter && 'js',
      jsidFilter && 'jsid',
      testFilter && 'test',
    ].filter(Boolean);
    let base = selectedTypes.length
      ? dashRows.filter((r) => selectedTypes.some((type) => matchesProjectType(r.project_number, type)))
      : dashRows;
    if (statusFilter) {
      const inc = STATUS_SUMMARY.find((s) => s.key === statusFilter);
      if (inc) base = base.filter(inc.match);
    }
    const counts = {};
    for (const key of excludeFilter) {
      const s = STATUS_SUMMARY.find((item) => item.key === key);
      if (!s) continue;
      // งาน/การเงินยังไม่เสร็จ = ยังมีภาระผูกพันจริงเสมอ ห้ามนับรวม exclude แค่เพราะมีบันทึก
      // นิติกรรมไว้ — ให้ตรงกับ matchKey ใน ProjectDashboardTable.js
      if (LEGAL_EXCLUDE_KEYS.has(key)) {
        counts[key] = base.filter(
          (r) => s.match(r) && r.work_status === 'done' && r.finance_status === 'completed_po',
        ).length;
      } else {
        counts[key] = base.filter(s.match).length;
      }
    }
    return counts;
  }, [excludeFilter, dashRows, jsFilter, jsidFilter, testFilter, statusFilter]);

  const filteredDashRows = useMemo(() => {
    let rows = dashRows;
    const selectedTypes = [
      jsFilter && 'js',
      jsidFilter && 'jsid',
      testFilter && 'test',
    ].filter(Boolean);
    if (selectedTypes.length) {
      rows = rows.filter((r) =>
        selectedTypes.some((type) => matchesProjectType(r.project_number, type)),
      );
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      rows = rows.filter(
        (r) =>
          (r.project_number || '').toLowerCase().includes(q) ||
          (r.name || '').toLowerCase().includes(q) ||
          (r.client_id?.name || '').toLowerCase().includes(q),
      );
    }
    if (customerFilter) {
      const q = customerFilter.toLowerCase();
      rows = rows.filter((r) =>
        (r.client_id?.name || r.customer || '').toLowerCase().includes(q),
      );
    }
    if (projectFilter) {
      rows = rows.filter((r) => r.project_number === projectFilter);
    }
    if (statusFilter) {
      rows = rows.filter((r) => {
        const ws = r.work_status || '';
        const fs = r.finance_status || '';
        const lw = Boolean(r.legal_warranty);
        const ld = Boolean(r.legal_debt);
        const ll = Boolean(r.legal_lawsuit);
        const hasCom = ws !== 'done' || fs !== 'completed_po' || lw || ld || ll;
        if (statusFilter === 'has_commitment') return hasCom;
        if (statusFilter === 'no_commitment') return !hasCom;
        if (statusFilter === 'work_waiting') return !ws || ws === 'waiting';
        if (statusFilter === 'work_in_progress') return ws === 'in_progress';
        if (statusFilter === 'work_done') return ws === 'done';
        if (statusFilter === 'finance_none') return !fs || fs === 'none';
        if (statusFilter === 'finance_partial') return fs === 'partial';
        if (statusFilter === 'finance_billed_waiting')
          return fs === 'billed_waiting';
        if (statusFilter === 'finance_last_installment')
          return fs === 'last_installment';
        if (statusFilter === 'finance_completed_po')
          return fs === 'completed_po';
        if (statusFilter === 'bg_no_bg_usage')
          return (r.bg_usage_category || '') === 'no_bg_usage';
        if (statusFilter === 'legal_warranty') return lw;
        if (statusFilter === 'legal_debt') return ld;
        if (statusFilter === 'legal_lawsuit') return ll;
        if (statusFilter === 'col9_positive') {
          const po = r.include_vat ? (r.cost || 0) - (r.vat || 0) : r.cost || 0;
          const col9 =
            po -
            (r.received || 0) -
            (r.seed_received || 0) -
            (r.billed_waiting || 0) -
            (r.seed_billed_waiting || 0);
          return Math.round(col9 * 100) > 0;
        }
        if (statusFilter === 'col7_positive') {
          return (
            Math.round(
              ((r.billed_waiting || 0) + (r.seed_billed_waiting || 0)) * 100,
            ) > 0
          );
        }
        if (statusFilter === 'col9_or_col7_nonzero') {
          const po = r.include_vat ? (r.cost || 0) - (r.vat || 0) : r.cost || 0;
          const col9 =
            po -
            (r.received || 0) -
            (r.seed_received || 0) -
            (r.billed_waiting || 0) -
            (r.seed_billed_waiting || 0);
          const col7 = (r.billed_waiting || 0) + (r.seed_billed_waiting || 0);
          return Math.round(col9 * 100) !== 0 || Math.round(col7 * 100) !== 0;
        }
        return true;
      });
    }
    if (excludeFilter.size > 0) {
      const excludeKeys = [...excludeFilter];
      rows = rows.filter(
        (r) =>
          !excludeKeys.some((key) => {
            const s = STATUS_SUMMARY.find((item) => item.key === key);
            if (s) return s.match(r);
            if (key === 'col9_positive') {
              const po = r.include_vat
                ? (r.cost || 0) - (r.vat || 0)
                : r.cost || 0;
              const col9 =
                po -
                (r.received || 0) -
                (r.seed_received || 0) -
                (r.billed_waiting || 0) -
                (r.seed_billed_waiting || 0);
              return Math.round(col9 * 100) > 0;
            }
            if (key === 'col7_positive')
              return (
                Math.round(
                  ((r.billed_waiting || 0) + (r.seed_billed_waiting || 0)) *
                    100,
                ) > 0
              );
            return false;
          }),
      );
    }
    return rows;
  }, [
    dashRows,
    searchTerm,
    statusFilter,
    jsFilter,
    jsidFilter,
    testFilter,
    excludeFilter,
    customerFilter,
    projectFilter,
  ]);

  const renderSummaryCards = () => {
    // ใช้ตัวเลขจาก ProjectDashboardTable โดยตรง เพื่อให้ตรงกันเสมอ
    const card1 = dashTotals?.poRemain ?? 0; // col4 (ยอดค้างส่งงานจาก PO)
    const card2 = includeLawsuitInCard2
      ? dashTotals?.col17 ?? 0
      : (dashTotals?.col17 ?? 0) - (dashTotals?.col17Lawsuit ?? 0); // col14 (สรุปอนาคตต้องจ่ายอีกรวม)
    const card3 = card1 - card2;
    const card4 = dashTotals?.billed ?? 0; // col2 (วางบิลแล้วรอรับเงิน, ไม่รวมโปรเจกต์ฟ้องร้อง)
    const card5 = card3 + card4;
    const cardLawsuit = dashTotals?.billedLawsuit ?? 0; // col2 เฉพาะโปรเจกต์ฟ้องร้อง แยกออกจากยอดรวม

    return (
      <Box sx={{ mb: 1.5 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1}
          useFlexGap
          sx={{ flexWrap: 'wrap', gap: 1 }}
        >
          {/* spacer โปร่งใสฝั่งซ้าย ขนาดเท่าป้ายฟ้องร้องฝั่งขวาเป๊ะ (JSX ก้อนเดียวกัน) — กัน
              ป้ายเดิม (card1...Total Cash In) เลื่อนตำแหน่งตอนป้ายฟ้องร้องโผล่/หาย เพราะทั้งแถบใช้
              justifyContent="center" จัดกึ่งกลางทั้งก้อนรวมกัน ถ้าไม่มี spacer นี้ถ่วงน้ำหนักไว้
              ป้ายเดิมทั้งหมดจะขยับซ้ายทุกครั้งที่ป้ายฟ้องร้อง (ฝั่งขวา) โผล่ขึ้นมา */}
          {showLawsuitCard && cardLawsuit !== 0 && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ visibility: 'hidden' }}
              aria-hidden="true"
            >
              <Op>+</Op>
              <SCard
                label={L('ฟ้องร้อง', 'Lawsuit')}
                value={cardLawsuit}
                color="#991b1b"
                col={L('ไม่รวมยอดรวม', 'Excluded from Total')}
              />
            </Stack>
          )}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              border: '1.5px solid rgba(37,99,235,0.25)',
              borderRadius: 2,
              px: 1.5,
              py: 1,
            }}
          >
            <SCard
              label={L('ยอดค้างส่งงานจาก PO', 'PO Remaining')}
              value={card1}
              color="#2563eb"
              col={showColNums ? 'col 4' : undefined}
              active={statusFilter === 'col9_positive'}
              onClick={() => {
                if (statusFilter === 'col9_positive') {
                  setStatusFilter('has_commitment');
                  setJsFilter(true);
                  setJsidFilter(false);
                  setTestFilter(false);
                } else {
                  // ไม่ปิด jsFilter — เดิม status filter อื่นมาแทนที่ JS_ เท่านั้นเงียบๆ ทั้งที่เป็นคนละ
                  // เงื่อนไขกัน (JS_ ตั้งแต่เข้าเพจ vs status ที่ user กดเพิ่มทีหลัง) การกดเพิ่มไม่ควรลบ
                  // เงื่อนไข JS_ เดิมทิ้ง — col9_positive เองก็เช็ค isJs ซ้อนอยู่แล้วในตัว ไม่ต้องพึ่ง
                  // jsFilter แยกก็จริง แต่ป้าย "JS_ เท่านั้น" ต้องยังโชว์อยู่ต่อเนื่องตามที่ user ยืนยัน
                  setStatusFilter('col9_positive');
                  setExcludeFilter((prev) => {
                    const s = new Set(prev);
                    s.delete('col9_positive');
                    return s;
                  });
                }
              }}
            />
            <Op>−</Op>
            <SCard
              label={L('สรุปอนาคตต้องจ่ายอีกรวม', 'Total Payable')}
              value={card2}
              color="#d97706"
              col={showColNums ? 'col 14' : undefined}
            />
            <Op>=</Op>
            <SCard label={L('ส่วนต่าง', 'Difference')} value={card3} color="#0891b2" />
          </Stack>
          <Op>+</Op>
          <SCard
            label={L('วางบิลแล้ว [ รวม VAT ]', 'Invoiced [ VAT incl ]')}
            value={card4}
            color="#059669"
            col={showColNums ? 'col 2' : undefined}
            active={statusFilter === 'col7_positive'}
            onClick={() => {
              if (statusFilter === 'col7_positive') {
                deactivateForceGroup1Filter();
              } else {
                activateForceGroup1Filter('col7_positive');
              }
            }}
          />
          <Op>=</Op>
          <SCard
            label={L('เงินสดรับรวม', 'Total Cash In')}
            value={card5}
            color="#7c3aed"
            col={L('ถ้าเสร็จและรับเงินครบ', 'If Complete & Paid')}
            active={statusFilter === 'col9_or_col7_nonzero'}
            onClick={() => {
              if (statusFilter === 'col9_or_col7_nonzero') {
                deactivateForceGroup1Filter();
              } else {
                activateForceGroup1Filter('col9_or_col7_nonzero');
              }
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              setLawsuitMenuAnchor(e.currentTarget);
            }}
          />
          {showLawsuitCard && cardLawsuit !== 0 && (
            <>
              <Op>+</Op>
              <SCard
                label={L('ฟ้องร้อง', 'Lawsuit')}
                value={cardLawsuit}
                color="#991b1b"
                col={L('ไม่รวมยอดรวม', 'Excluded from Total')}
                active={statusFilter === 'legal_lawsuit'}
                onClick={() => {
                  if (statusFilter === 'legal_lawsuit') {
                    setStatusFilter('has_commitment');
                    setJsFilter(true);
                    setJsidFilter(false);
                    setTestFilter(false);
                  } else {
                    setStatusFilter('legal_lawsuit');
                    setJsFilter(false);
                    setJsidFilter(false);
                    setTestFilter(false);
                    setExcludeFilter((prev) => {
                      const s = new Set(prev);
                      s.delete('legal_lawsuit');
                      return s;
                    });
                  }
                }}
              />
            </>
          )}
        </Stack>
        <Popover
          open={Boolean(lawsuitMenuAnchor)}
          anchorEl={lawsuitMenuAnchor}
          onClose={() => setLawsuitMenuAnchor(null)}
          anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
          transformOrigin={{ vertical: 'center', horizontal: 'left' }}
        >
          <Box sx={{ p: 1.5, minWidth: 260 }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}
            >
              การแสดงผล "ฟ้องร้อง" ในป้ายสรุป
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={showLawsuitCard}
                  onChange={(e) => {
                    setShowLawsuitCard(e.target.checked);
                    // popover เปิดจากขวาของการ์ด "Total Cash In" ตำแหน่งเดียวกับที่ป้ายฟ้องร้องโผล่
                    // พอดี เลยบังป้ายไว้จนกว่าจะปิดเอง — ปิด popover ทันทีทุกครั้งที่กดเลือก (ไม่ว่าติ๊ก
                    // เข้าหรือออก) จะได้เห็นผลลัพธ์ทันทีโดยไม่ต้องคลิกออกไปปิดเอง
                    setLawsuitMenuAnchor(null);
                  }}
                />
              }
              label="แสดงป้ายฟ้องร้อง"
              sx={{ display: 'flex' }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={includeLawsuitInCard2}
                  onChange={(e) => {
                    setIncludeLawsuitInCard2(e.target.checked);
                    setLawsuitMenuAnchor(null);
                  }}
                />
              }
              label="รวมยอดค้างจ่ายในงานที่ฟ้องร้อง"
              sx={{ display: 'flex' }}
            />
          </Box>
        </Popover>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          useFlexGap
          sx={{ flexWrap: 'wrap', gap: 1, mt: 2 }}
        >
          <SearchField
            name="project-search-dashboard"
            type="search"
            autoComplete="off"
            label={L('ค้นหาโครงการ', 'Search Project')}
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleDbSearch();
            }}
            placeholder={L('รหัส · ชื่อโครงการ · ลูกค้า · หมายเหตุ', 'Code · Project · Customer · Notes')}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '0.78rem',
              },
              '& .MuiInputLabel-root': { fontSize: '0.78rem' },
              '& .MuiInputBase-input::placeholder': { fontSize: '0.75rem' },
              width: 480,
              flexShrink: 0,
            }}
          />
          <Stack
            direction="row"
            alignItems="flex-end"
            justifyContent="center"
            flexWrap="wrap"
            sx={{ flex: 1, gap: 0.75, rowGap: 0.5, alignSelf: 'stretch' }}
          >
            {/* ป้าย sort (เทียบกับ default sort) — ป้าย filter ที่ active ตั้งแต่เข้าเพจ
                (Default/JS_/status/exclude) อยู่แถวล่าง (แถบตัวกรอง ProjectDashboardTable.js)
                เสมอ ไม่มีเงื่อนไข เพราะเป็นค่าที่กรองอยู่ตั้งแต่เข้าเพจ P5 ไม่ใช่สิ่งที่ user มาสั่งเพิ่ม
                ทีหลัง — ป้ายอื่นที่ user สั่งเพิ่มทีหลัง (เช่นคลิกชื่อผู้ว่าจ้าง/โครงการในตารางเพื่อกรอง)
                ต่อท้ายป้าย sort ตรงนี้เสมอ (ถ้า sort chip ไม่โผล่ ก็ขึ้นในตำแหน่งเดียวกันแทน เพราะ
                justifyContent="center" ของ Stack นี้จัดกึ่งกลางเนื้อหาที่มีอยู่ให้เองอัตโนมัติ) */}
            {!isDefaultSort && (
              <Chip
                label={L('↺ คืนค่าการเรียงลำดับ', '↺ Reset Sort')}
                onClick={() => dashboardTableRef.current?.resetSort()}
                size="small"
                sx={{
                  height: 16.5,
                  fontWeight: 600,
                  cursor: 'pointer',
                  borderRadius: '6px',
                  bgcolor: 'rgba(192,150,20,0.92)',
                  color: '#fff',
                  border: '1px solid #d4a017',
                  '& .MuiChip-label': { px: 0.8, fontSize: '0.59rem' },
                  '&:hover': { bgcolor: 'rgba(155,120,15,0.95)' },
                }}
              />
            )}
            {customerFilter && (
              <Chip
                label={`${L('ผู้ว่าจ้าง', 'Customer')}: ${customerFilter}`}
                onDelete={() => setCustomerFilter('')}
                size="small"
                sx={{
                  height: 16.5,
                  fontWeight: 600,
                  borderRadius: '6px',
                  bgcolor: '#eff6ff',
                  color: '#1d4ed8',
                  border: '1px solid #bfdbfe',
                  '& .MuiChip-label': { px: 0.8, fontSize: '0.59rem' },
                  '& .MuiChip-deleteIcon': { fontSize: 10.5, mr: 0.25 },
                }}
              />
            )}
            {projectFilter && (
              <Chip
                label={`${L('โครงการ', 'Project')}: ${projectFilter}`}
                onDelete={() => setProjectFilter('')}
                size="small"
                sx={{
                  height: 16.5,
                  fontWeight: 600,
                  borderRadius: '6px',
                  bgcolor: '#eff6ff',
                  color: '#1d4ed8',
                  border: '1px solid #bfdbfe',
                  '& .MuiChip-label': { px: 0.8, fontSize: '0.59rem' },
                  '& .MuiChip-deleteIcon': { fontSize: 10.5, mr: 0.25 },
                }}
              />
            )}
            {statusFilter &&
              statusFilter !== 'has_commitment' &&
              (() => {
                const meta =
                  FILTER_META[statusFilter] ||
                  STATUS_MAP[statusFilter] ||
                  {};
                const label = meta.label || statusFilter;
                const color = meta.color || '#64748b';
                return (
                  <Chip
                    label={`${label} · ${filteredDashRows.length}`}
                    onDelete={() => setStatusFilter('has_commitment')}
                    size="small"
                    sx={{
                      height: 16.5,
                      fontWeight: 600,
                      borderRadius: '6px',
                      bgcolor: color,
                      color: '#fff',
                      border: `1px solid ${color}`,
                      '& .MuiChip-label': { px: 0.8, fontSize: '0.59rem' },
                      '& .MuiChip-deleteIcon': {
                        fontSize: 10.5,
                        mr: 0.25,
                        color: '#fff',
                      },
                    }}
                  />
                );
              })()}
            {[...excludedProjects].map((pn) => (
              <Chip
                key={`ex-proj-${pn}`}
                label={`${L('ไม่รวมโครงการ', 'Exclude Project')}: ${pn}`}
                onDelete={() => dashboardTableRef.current?.removeExcludedProject(pn)}
                size="small"
                sx={{
                  height: 16.5,
                  fontWeight: 600,
                  borderRadius: '6px',
                  bgcolor: '#f3e8ff',
                  color: '#7c3aed',
                  border: '1px solid #c4b5fd',
                  '& .MuiChip-label': { px: 0.8, fontSize: '0.59rem' },
                  '& .MuiChip-deleteIcon': { fontSize: 10.5, mr: 0.25 },
                }}
              />
            ))}
            {[...excludedCustomers].map((cn) => (
              <Chip
                key={`ex-cust-${cn}`}
                label={`${L('ไม่รวมผู้ว่าจ้าง', 'Exclude Customer')}: ${cn}`}
                onDelete={() => dashboardTableRef.current?.removeExcludedCustomer(cn)}
                size="small"
                sx={{
                  height: 16.5,
                  fontWeight: 600,
                  borderRadius: '6px',
                  bgcolor: '#f3e8ff',
                  color: '#7c3aed',
                  border: '1px solid #c4b5fd',
                  '& .MuiChip-label': { px: 0.8, fontSize: '0.59rem' },
                  '& .MuiChip-deleteIcon': { fontSize: 10.5, mr: 0.25 },
                }}
              />
            ))}
            {[...excludedZeroFields].map((key) => (
              <Chip
                key={`ex-zero-${key}`}
                label={`${L('ไม่รวม 0/ว่าง', 'Exclude 0/Empty')}: ${ZERO_EXCLUDE_LABELS[key] || key}`}
                onDelete={() => dashboardTableRef.current?.removeZeroExclude(key)}
                size="small"
                sx={{
                  height: 16.5,
                  fontWeight: 600,
                  borderRadius: '6px',
                  bgcolor: '#f3e8ff',
                  color: '#7c3aed',
                  border: '1px solid #c4b5fd',
                  '& .MuiChip-label': { px: 0.8, fontSize: '0.59rem' },
                  '& .MuiChip-deleteIcon': { fontSize: 10.5, mr: 0.25 },
                }}
              />
            ))}
          </Stack>
          <Button
            variant={normalView ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setNormalView((v) => !v)}
            sx={{
              borderRadius: 1.5,
              height: 25.2,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              minWidth: 0,
              px: 1,
              fontSize: '0.6rem',
            }}
          >
            {normalView ? '🔍 Auto Zoom' : '1:1'}
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: '0.7rem !important' }} />}
            onClick={() => setCreateOpen(true)}
            sx={{
              borderRadius: 1.5,
              height: 25.2,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              px: 1,
              fontSize: '0.6rem',
              '& .MuiButton-startIcon': { mr: 0.4 },
            }}
          >
            {L('เพิ่มโครงการใหม่', 'New Project')}
          </Button>
          <Button
            variant="outlined"
            color="success"
            size="small"
            startIcon={<FileDownloadIcon sx={{ fontSize: '0.7rem !important' }} />}
            onClick={() => dashboardTableRef.current?.exportExcel()}
            sx={{
              borderRadius: 1.5,
              height: 25.2,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              px: 1,
              fontSize: '0.6rem',
              '& .MuiButton-startIcon': { mr: 0.4 },
            }}
          >
            Export Excel
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PersonAddAlt1Icon sx={{ fontSize: '0.7rem !important' }} />}
            onClick={handleAddClientOpen}
            sx={{
              borderRadius: 1.5,
              height: 25.2,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              px: 1,
              fontSize: '0.6rem',
              '& .MuiButton-startIcon': { mr: 0.4 },
            }}
          >
            {L('เพิ่มลูกค้า', 'Add Customer')}
          </Button>
          {searchTerm.trim() && (
            <Button
              size="small"
              variant="outlined"
              onClick={handleDbSearch}
              disabled={dashLoading}
              sx={{
                borderRadius: 1.5,
                height: 25.2,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                px: 1,
                fontSize: '0.6rem',
                borderColor: '#2563eb',
                color: '#2563eb',
                '&:hover': { bgcolor: '#eff6ff', borderColor: '#1d4ed8' },
              }}
            >
              {dashLoading ? 'กำลังค้นหา...' : '🔍 ค้นหาทุก record ใน DB'}
            </Button>
          )}
          <Tooltip
            title={
              lang === 'en'
                ? 'สลับป้ายกำกับในหน้านี้เป็นภาษาไทย'
                : 'Switch labels on this page to English'
            }
          >
            <Button
              variant="outlined"
              size="small"
              onClick={toggleLang}
              sx={{
                borderRadius: 1.5,
                height: 25.2,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                minWidth: 0,
                px: 1,
                fontSize: '0.6rem',
              }}
            >
              {lang === 'en' ? '🇹🇭 ไทย' : '🇬🇧 EN'}
            </Button>
          </Tooltip>
        </Stack>
      </Box>
    );
  };

  const renderDashboard = () => (
    <Box>
      {renderSummaryCards()}
      <Card
        sx={{
          borderRadius: 0,
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          overflow: 'hidden',
        }}
      >
        <ProjectDashboardTable
          ref={dashboardTableRef}
          showGroup1={showGroup1}
          onShowGroup1Change={setShowGroup1}
          showColNums={showColNums}
          onShowColNumsChange={setShowColNums}
          initialSortCriteria={dashboardSortCriteria}
          onSortCriteriaChange={setDashboardSortCriteria}
          onSortDefaultChange={setIsDefaultSort}
          excludedZeroFields={excludedZeroFields}
          onExcludedZeroFieldsChange={setExcludedZeroFields}
          excludedProjects={excludedProjects}
          onExcludedProjectsChange={setExcludedProjects}
          excludedCustomers={excludedCustomers}
          onExcludedCustomersChange={setExcludedCustomers}
          pageScale={pageScale}
          projects={{ rows: dashRows, total: dashRows.length }}
          isLoading={dashLoading}
          isAdmin={isAdmin}
          onClickProject={handleOpenProject}
          searchTerm={searchTerm}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          jsFilter={jsFilter}
          onJsFilterChange={setJsFilter}
          jsidFilter={jsidFilter}
          onJsidFilterChange={setJsidFilter}
          testFilter={testFilter}
          onTestFilterChange={setTestFilter}
          excludeFilter={excludeFilter}
          onExcludeFilterChange={setExcludeFilter}
          defaultExcludeFilter={DEFAULT_EXCLUDE_FILTER}
          excludeCounts={excludeCounts}
          customerFilter={customerFilter}
          onCustomerFilterChange={setCustomerFilter}
          projectFilter={projectFilter}
          onProjectFilterChange={setProjectFilter}
          statusSummaryChips={buildStatusChips()}
          onTotalsChange={setDashTotals}
          onVisibleIdsChange={setVisibleIds}
          pinnedProjectId={selectedId}
          onActionRequiredChange={handleActionRequiredChange}
        />
      </Card>
    </Box>
  );

  const renderList = () => (
    <Card
      sx={{
        borderRadius: 2,
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        overflow: 'hidden',
      }}
    >
      <ProjectTable
        projects={projects}
        handleOnclickDetail={handleOnclickDetail}
        onSelectProject={handleOpenProject}
        page={page}
        setPage={setPage}
        size={size}
        setSize={setSize}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          p: 1.5,
          bgcolor: alpha(theme.palette.background.default, 0.6),
        }}
      >
        <Typography variant="caption" color="textSecondary">
          แสดงข้อมูลทั้งหมด {projects?.total || 0} รายการ (หน้า {page} จาก{' '}
          {Math.ceil((projects?.total || 0) / size)})
        </Typography>
        <Typography variant="caption" color="textSecondary">
          อัปเดทล่าสุด: {dayjs().format('DD/MM/YYYY HH:mm')}
        </Typography>
      </Box>
    </Card>
  );

  return (
    <div ref={pageRef}>
      <Tabs
        value={activeTab}
        onChange={(_e, v) => setActiveTab(v)}
        sx={{
          minHeight: 36,
          mt: -5,
          mb: 1,
          overflow: 'visible',
          '& .MuiTabs-scroller': { overflow: 'visible !important' },
          '& .MuiTab-root': { minHeight: 36, py: 0.5, position: 'relative', overflow: 'visible' },
        }}
      >
        <Tab
          icon={<DashboardIcon fontSize="small" />}
          iconPosition="start"
          label={
            <>
              <Box sx={{ position: 'absolute', bottom: -14, left: '50%', transform: 'translateX(-50%)' }}>
                <TabNumTag>T-12</TabNumTag>
              </Box>
              {L('ตารางโครงการ', 'Project Table')}
            </>
          }
          sx={{ fontSize: '0.78rem', minWidth: 0 }}
        />
        <Tab
          icon={<InsightsIcon fontSize="small" />}
          iconPosition="start"
          label={
            <>
              <Box sx={{ position: 'absolute', bottom: -14, left: '50%', transform: 'translateX(-50%)' }}>
                <TabNumTag>T-13</TabNumTag>
              </Box>
              {L('ภาพรวม PO', 'PO Overview')}
            </>
          }
          sx={{ fontSize: '0.78rem', minWidth: 0 }}
        />
      </Tabs>
      <DevTabBadge tabNum={12} name="ตารางโครงการ (P5 tab)" active={activeTab === 0} />
      <DevTabBadge tabNum={13} name="ภาพรวม PO (P5 tab)" active={activeTab === 1} />

      {activeTab === 0 && renderDashboard()}
      {activeTab === 1 && (
        // Project.js layout ปิด document.body/html scroll ไว้ตั้งใจ (ให้ ProjectDashboardTable
        // จัดการ scroll ของตัวเองผ่าน maxHeight:'693px' บน TableContainer) — แท็บนี้ไม่มี scroll
        // container ของตัวเอง เนื้อหาที่สูงเกินจอเลยโดนตัดมองไม่เห็น/scroll ไม่ได้เลย ใช้กรอบ scroll
        // ของตัวเอง — ใช้ calc(100vh - ...) แทนตัวเลข px ตายตัวแบบตาราง เพราะเนื้อหาแท็บนี้สูงกว่า
        // 693px มาก (มีการ์ด 3 ใบเพิ่มด้านล่าง) เผื่อพื้นที่โชว์ให้เต็มจอเท่าที่มีจริงแทนที่จะกั้นไว้
        // สั้นเกินจำเป็น (ยืนยันแล้วว่าช่องว่างท้ายเนื้อหามาจากค่านี้จริง — ตั้งไว้ที่ 50)
        <Box sx={{ maxHeight: 'calc(100vh - 50px)', overflowY: 'auto', pr: 3 }}>
          <POOverviewTab
            rows={
              defaultDashRowsRef.current?.length
                ? defaultDashRowsRef.current
                : dashRows
            }
            onOpenProject={handleOpenProject}
          />
        </Box>
      )}

      <DevWinBadge num={111} name="Project · Popup" open={!!selectedId} />
      <Dialog
        open={!!selectedId}
        onClose={handleCloseProject}
        maxWidth={false}
        sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
        PaperComponent={DraggablePaper}
        PaperProps={{
          sx: {
            mt: '40px',
            maxHeight: 'calc(100vh - 56px)',
            width: 'auto',
            // กัน popup แคบลงตอนอยู่แท็บ "แดชบอร์ด" (เนื้อหาแคบกว่าแท็บ "รายละเอียด")
            // ให้กว้างเท่ากันเสมอไม่ว่าจะสลับไปแท็บไหน แต่ยังขยายกว้างกว่านี้ได้ถ้าเนื้อหาแท็บ
            // "รายละเอียด" ต้องการพื้นที่มากกว่า (ตาราง budget ที่กว้างมาก)
            minWidth: 1215,
            maxWidth: 'calc(100vw - 32px)',
          },
        }}
        hideBackdrop
      >
        <DialogContent sx={{ p: 0 }}>
          {selectedId && (
            <ProjectPopup
              key={selectedId}
              propId={selectedId}
              onClose={handleCloseProject}
              onSaved={handleSavedProject}
              onPopOut={() => handlePopOutProject(selectedId)}
              onPrev={visibleIndex > 0 ? handlePrevProject : undefined}
              onNext={
                visibleIndex >= 0 && visibleIndex < visibleIds.length - 1
                  ? handleNextProject
                  : undefined
              }
              navIndex={visibleIndex}
              navTotal={visibleIds.length}
              filterChips={activeFilterChips}
              initialData={selectedRow}
              sheet={popupSheet}
              onSheetChange={setPopupSheet}
            />
          )}
        </DialogContent>
      </Dialog>

      <ProjectEditPopup
        open={createOpen}
        propId={null}
        title="เพิ่มโครงการใหม่"
        onClose={() => setCreateOpen(false)}
        onSaved={() => {
          setCreateOpen(false);
          dispatch(
            actions.projectAll({ page, size, name: searchTerm || undefined }),
          );
        }}
      />

      <ContactFormModal
        open={addClientOpen}
        onClose={handleAddClientClose}
        title="เพิ่มลูกค้าใหม่"
        subtitle="กรอกข้อมูลลูกค้าองค์กรหรือบุคคลธรรมดา"
        onSubmit={handleAddClientSubmit(onAddClientSubmit)}
        error={addClientSubmitError}
      >
        <ContactFormFields
          control={addClientControl}
          cascade={addClientCascade}
          setZipSelections={setAddClientZipSelections}
        />
      </ContactFormModal>
    </div>
  );
}
